import moment from "moment";
import { useEffect, useState } from "react";

import ArrowDown from "@material-ui/icons/ArrowDropDown";
import ArrowRight from "@material-ui/icons/ArrowRight";
import styled, { createGlobalStyle } from "styled-components";
import fetchClient from "../api/fetchClient";
import Loader from "../components/Loader";

const MESSAGE_PREVIEW = 140;

const ViewerGlobalStyles = createGlobalStyle`
  body {
    margin: 0;
    background: #e9efff;
    font-family: 'Inter', sans-serif;
  }
`;

export default function LogEvents(props) {
  const [logEvents, setLogEvents] = useState([]);
  const [openeds, setOpeneds] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { groupLog, empresa, type, viceri = false, senior = false, ariba = false, ifs = false } = props;

  const [backPage, setBackPage] = useState({
    has: false,
    link: null,
  });

  const [nextPage, setNextPage] = useState({
    has: false,
    link: null,
  });

  async function getEvents(token = null) {
    try {
      setIsLoading(true);
      let data;

      if (viceri) {
        data = (
          await fetchClient.get(
            `viceri/getLog/${groupLog}?type=${type}${
              token ? `&nextToken=${token}` : ""
            }`
          )
        ).data;
      } else if (senior) {
        data = (
          await fetchClient.get(
            `senior/getLog/${empresa}/${groupLog}?type=${type}${
              token ? `&nextToken=${token}` : ""
            }`
          )
        ).data;
      } else if (ariba) {
        data = (
          await fetchClient.get(
            `ariba/getLog/${empresa}/${groupLog}?type=${type}${
              token ? `&nextToken=${token}` : ""
            }`
          )
        ).data;
      } else if (ifs) {
        data = (
          await fetchClient.get(
            `ifs/getLog/${empresa}?type=${type}&logStreamName=${encodeURIComponent(groupLog)}${
              token ? `&nextToken=${token}` : ""
            }`
          )
        ).data;
      } else {
        data = (
          await fetchClient.get(
            `getLog/${empresa}/${groupLog}?type=${type}${
              token ? `&nextToken=${token}` : ""
            }`
          )
        ).data;
      }

      setNextPage(
        data.nextForwardToken
          ? { has: true, link: data.nextForwardToken }
          : { has: false, link: null }
      );
      setBackPage(
        data.nextBackwardToken
          ? { has: true, link: data.nextBackwardToken }
          : { has: false, link: null }
      );

      setLogEvents(data.events || []);
      setError(null);
    } catch (e) {
      setError("Nao foi possivel encontrar os logs deste evento.");
    }
    setIsLoading(false);
  }

  useEffect(() => {
    getEvents();

    //eslint-disable-next-line
  }, [groupLog, empresa, type]);

  const toggleRow = (index) => {
    if (openeds.includes(index)) {
      setOpeneds(openeds.filter((opened) => opened !== index));
    } else {
      setOpeneds([...openeds, index]);
    }
  };

  return (
    <>
      <ViewerGlobalStyles />
      <ViewerSurface>
        <ViewerContainer>
          {isLoading ? (
            <Loader />
          ) : error ? (
            <ErrorState>{error}</ErrorState>
          ) : (
          <>
            <ViewerHeader>
              <ViewerInfo>
                <ViewerTitle>Eventos do stream</ViewerTitle>
                <ViewerSubtitle>
                  Stream {groupLog} | Empresa {empresa || (viceri ? "Viceri" : ariba ? "Ariba" : "Concur")} | Tipo{" "}
                  {type?.toUpperCase() || "-"}
                </ViewerSubtitle>
              </ViewerInfo>
              <ViewerActions>
                <NavButton
                  disabled={!backPage.has}
                  onClick={() => getEvents(backPage.link)}
                >
                  Anterior
                </NavButton>
                <NavButton
                  disabled={!nextPage.has}
                  onClick={() => getEvents(nextPage.link)}
                >
                  Proximo
                </NavButton>
              </ViewerActions>
            </ViewerHeader>
            {logEvents.length > 0 && (
              <TableHelper>
                <HelperTitle>
                  {logEvents.length} eventos carregados
                </HelperTitle>
                <HelperSubtitle>
                  Clique em uma linha para expandir o conteudo completo.
                </HelperSubtitle>
              </TableHelper>
            )}
            {logEvents.length === 0 ? (
              <EmptyState>Sem registros para este stream.</EmptyState>
            ) : (
              <EventsTableWrapper>
                <EventsTable>
                  <thead>
                    <tr>
                      <EventsHead>Data</EventsHead>
                      <EventsHead>Evento</EventsHead>
                    </tr>
                  </thead>
                  <tbody>
                    {logEvents.map(({ timestamp, message }, index) => {
                      const expanded = openeds.includes(index);
                      const showToggle = message.length > MESSAGE_PREVIEW;
                      const preview = expanded
                        ? message
                        : showToggle
                        ? `${message.slice(0, MESSAGE_PREVIEW)}…`
                        : message;

                      return (
                        <EventsRow key={index} $expanded={expanded}>
                          <EventsCell>
                            <EventsDate>
                              {moment(timestamp).format("DD/MM/YYYY")}
                            </EventsDate>
                            <EventsTime>
                              {moment(timestamp).format("HH:mm:ss")}
                            </EventsTime>
                          </EventsCell>
                          <MessageCell $expanded={expanded}>
                            <MessageHeader
                              onClick={() => showToggle && toggleRow(index)}
                              $clickable={showToggle}
                            >
                              {showToggle &&
                                (expanded ? <ArrowDown /> : <ArrowRight />)}
                              <MessageText>{preview}</MessageText>
                            </MessageHeader>
                            {expanded && (
                              <FullMessage>
                                <pre>{message}</pre>
                              </FullMessage>
                            )}
                          </MessageCell>
                        </EventsRow>
                      );
                    })}
                  </tbody>
                </EventsTable>
              </EventsTableWrapper>
            )}
          </>
        )}
        </ViewerContainer>
      </ViewerSurface>
    </>
  );
}

const ViewerSurface = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 32px;
  background: linear-gradient(135deg, #dfe8ff 0%, #f8fbff 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
  @media (max-width: 1024px) {
    padding: 16px;
  }
`;

const ViewerContainer = styled.div`
  width: 100%;
  max-width: 1180px;
  min-width: 960px;
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 35px 80px rgba(15, 23, 42, 0.22);
  padding: 32px 40px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 85vh;
  box-sizing: border-box;
  @media (max-width: 1024px) {
    min-width: auto;
    padding: 24px;
  }
`;

const ViewerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
`;

const ViewerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ViewerTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  color: #111827;
`;

const ViewerSubtitle = styled.span`
  font-size: 15px;
  color: #4c5565;
  font-weight: 500;
`;

const ViewerActions = styled.div`
  display: flex;
  gap: 12px;
`;

const NavButton = styled.button`
  background: #00bcd4;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  font-size: 15px;
  &:hover:not(:disabled) {
    background: #00acc1;
    box-shadow: 0 10px 25px rgba(0, 188, 212, 0.25);
  }
  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
  }
`;

const TableHelper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HelperTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
`;

const HelperSubtitle = styled.span`
  font-size: 13px;
  color: #6b7280;
`;

const EventsTableWrapper = styled.div`
  width: 100%;
  max-height: calc(100vh - 220px);
  overflow: auto;
  border-radius: 20px;
  box-shadow: inset 0 0 0 1px #e5e7eb;
  background: linear-gradient(180deg, #fff 0%, #f8faff 100%);
  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5f5;
    border-radius: 999px;
  }
`;

const EventsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const EventsHead = styled.th`
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  padding: 18px 24px;
  border-bottom: 1px solid #eef2f7;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
`;

const EventsRow = styled.tr`
  border-bottom: 1px solid #f0f3f8;
  background: ${(props) => (props.$expanded ? "#f6fbff" : "#fff")};
  transition: background 0.2s ease;
`;

const EventsCell = styled.td`
  width: 220px;
  padding: 18px 24px;
  border-right: 1px solid #f0f3f8;
  vertical-align: top;
`;

const EventsDate = styled.div`
  font-weight: 600;
  color: #1f2933;
`;

const EventsTime = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const MessageCell = styled.td`
  padding: 18px 24px;
  background: ${(props) => (props.$expanded ? "#f9fbff" : "transparent")};
  transition: background 0.2s ease;
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  color: #0f172a;
  line-height: 1.6;
  font-size: 15px;
  svg {
    color: #00acc1;
    flex-shrink: 0;
  }
`;

const MessageText = styled.span`
  display: inline-block;
  word-break: break-word;
`;

const FullMessage = styled.div`
  margin-top: 12px;
  padding: 18px;
  background: #0b1220;
  border-radius: 12px;
  border: 1px solid #1f2a44;
  color: #e2e8f0;
  font-size: 14px;
  line-height: 1.65;
  max-height: 300px;
  overflow-y: auto;
  pre {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: "Fira Code", "Consolas", monospace;
    font-size: 13px;
  }
`;

const EmptyState = styled.div`
  width: 100%;
  padding: 48px;
  text-align: center;
  border: 2px dashed #d7deea;
  border-radius: 16px;
  color: #6b7280;
  background: #fff;
`;

const ErrorState = styled.div`
  width: 100%;
  padding: 32px;
  border-radius: 16px;
  background: #fff1f2;
  border: 1px solid #fda4af;
  color: #b91c1c;
  text-align: center;
`;
